import {
  EMAIL,
  EmbeddedMessage,
  IN_APP,
  PUSH,
  SMS,
  WEB_PUSH,
  WhatsApp,
} from 'constants/messageMediums';
import { TemplateType as TT } from 'constants/templateTypes';
import { BADGE_VARIANTS } from 'ui/badge/constants';
import { Delete } from 'ui/icons';
import { campaignStates } from 'utils/campaigns';
import { pxToRem } from 'utils/convert';
import { Column, StateFilters } from './types';
import { CampaignStateDefinitions } from './types/Campaign';
import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  PseudoApprovalStatusDefinition,
} from 'modules/graphql';
import { getQualityRatingLabel } from 'modules/templateIndex/utils';

export const SEARCH_TEXT_INPUT_DEBOUNCE_TIMEOUT_MS = 500;
const additionalCampaignStates = {
  STARTING: 'Starting',
  ABORTED: 'Aborted',
  ARCHIVED: 'Archived',
};

export const BADGE_VARIANTS_FOR_CAMPAIGN_STATUSES = {
  [campaignStates.DRAFT.value]: BADGE_VARIANTS.DRAFT,
  [campaignStates.FINISHED.value]: BADGE_VARIANTS.FINISHED,
  [campaignStates.READY.value]: BADGE_VARIANTS.READY,
  [campaignStates.RUNNING.value]: BADGE_VARIANTS.RUNNING,
  [campaignStates.SCHEDULED.value]: BADGE_VARIANTS.SCHEDULED,
  [campaignStates.RECURRING.value]: BADGE_VARIANTS.RECURRING,
  [campaignStates.RECALLING.value]: BADGE_VARIANTS.RECALL,
  [campaignStates.RECALLED.value]: BADGE_VARIANTS.RECALL,
  [additionalCampaignStates.STARTING]: BADGE_VARIANTS.RUNNING,
  [additionalCampaignStates.ABORTED]: BADGE_VARIANTS.ABORTED,
  [additionalCampaignStates.ARCHIVED]: BADGE_VARIANTS.FINISHED,
};

/**
  These represent back-end GraphQL enums for sorting
 */
export const SORT_BY_FIELD = {
  START_AT: 'StartAt',
  ID: 'Id',
  LIST_TYPE: 'ListType',
  NAME: 'Name',
  STATE: 'State',
  UPDATED_AT: 'UpdatedAt',
};

export const FILTER_PREFIXES = {
  CAMPAIGN_TYPE: 'campaign-type-',
  CHANNEL_TYPE: 'channel-type-',
  CREATOR: 'creator-',
  LABEL: 'label-',
  LIST: 'list-',
  MEDIUM: 'medium-',
  MODIFIER: 'modifier-',
  STATE: 'state-',
  WHATSAPP_TYPE: 'whatsapp-type-',
  WHATSAPP_STATUS: 'whatsapp-status-',
  WHATSAPP_QUALITY_RATING: 'whatsapp-quality-rating-',
};

export const DELIVERY = 'Delivery';
export const ENGAGEMENT = 'Engagement';

export const CAMPAIGN_INDEX_COLUMNS: Column[] = [
  { id: SORT_BY_FIELD.NAME, label: 'Campaign name' },
  { id: SORT_BY_FIELD.STATE, label: 'Status' },
  { id: DELIVERY, label: DELIVERY },
  { id: ENGAGEMENT, label: ENGAGEMENT },
  { id: SORT_BY_FIELD.START_AT, label: 'Launch date' },
  { id: SORT_BY_FIELD.UPDATED_AT, label: 'Last modified' },
];

export enum CampaignFilterId {
  Label = 'Label',
  Medium = 'Medium',
  CampaignType = 'Campaign type',
  ChannelType = 'Channel type',
  AudienceList = 'Audience list',
  Status = 'Status',
  Alerts = 'Alerts',
  Creator = 'Creator',
  ModifiedBy = 'Modified by',
  TemplateType = 'Template type',
  TemplateStatus = 'Template status',
  TemplateQualityRating = 'Template quality rating',
  Placements = 'Placements',
}

export enum CampaignFilterPrefix {
  Label = 'label-',
  Medium = 'medium-',
  CampaignType = 'campaign-type-',
  ChannelType = 'channel-type-',
  AudienceList = 'audience-list-',
  Status = 'status-',
  Alerts = 'alert-',
  Creator = 'creator-',
  ModifiedBy = 'modified-by-',
  TemplateType = 'whatsapp-type-',
  TemplateStatus = 'whatsapp-status-',
  TemplateQualityRating = 'whatsapp-quality-rating-',
  Placements = 'placements-',
}

const CATEGORY_KEYS = {
  ALL: 'All mediums',
  WHATS_APP: 'WhatsApp',
  EMBEDDED: 'Embedded',
};

export const CAMPAIGN_FILTER_OPTIONS = [
  {
    label: 'Label',
    id: CampaignFilterId.Label,
    prefix: CampaignFilterPrefix.Label,
    queryLabel: 'name',
    queryId: 'id',
    isNumber: true,
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Medium',
    id: CampaignFilterId.Medium,
    prefix: CampaignFilterPrefix.Medium,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Campaign type',
    id: CampaignFilterId.CampaignType,
    prefix: CampaignFilterPrefix.CampaignType,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Channel type',
    id: CampaignFilterId.ChannelType,
    prefix: CampaignFilterPrefix.ChannelType,
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Audience list',
    id: CampaignFilterId.AudienceList,
    prefix: CampaignFilterPrefix.AudienceList,
    queryLabel: 'name',
    queryId: 'id',
    isNumber: true,
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Status',
    id: CampaignFilterId.Status,
    prefix: CampaignFilterPrefix.Status,
    queryLabel: 'name',
    queryId: 'name',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Alerts',
    id: CampaignFilterId.Alerts,
    prefix: CampaignFilterPrefix.Alerts,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Creator',
    id: CampaignFilterId.Creator,
    prefix: CampaignFilterPrefix.Creator,
    queryLabel: 'fullName',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Modified by',
    id: CampaignFilterId.ModifiedBy,
    prefix: CampaignFilterPrefix.ModifiedBy,
    queryLabel: 'fullName',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.ALL,
  },
  {
    label: 'Template type',
    id: CampaignFilterId.TemplateType,
    prefix: CampaignFilterPrefix.TemplateType,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.WHATS_APP,
  },
  {
    label: 'Template status',
    id: CampaignFilterId.TemplateStatus,
    prefix: CampaignFilterPrefix.TemplateStatus,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.WHATS_APP,
  },
  {
    label: 'Template quality rating',
    id: CampaignFilterId.TemplateQualityRating,
    prefix: CampaignFilterPrefix.TemplateQualityRating,
    queryLabel: 'label',
    queryId: 'id',
    forMedium: CATEGORY_KEYS.WHATS_APP,
  },
  {
    label: 'Placements',
    id: CampaignFilterId.Placements,
    prefix: CampaignFilterPrefix.Placements,
    queryLabel: 'label',
    queryId: 'id',
    isNumber: true,
    forMedium: CATEGORY_KEYS.EMBEDDED,
  },
];

export const FAVORITE_STATE_FILTER = {
  id: 'Favorites',
  label: 'Favorites',
};

export const CAMPAIGN_STATE_FILTERS: StateFilters[] = [
  {
    id: '',
    label: 'All',
    sortOrder: 0,
    filters: [],
    query: [
      'Draft',
      'Ready',
      'Running',
      'Scheduled',
      'Recurring',
      'Finished',
      'Aborted',
      'Recalling',
      'Recalled',
    ],
  },
  {
    id: 'Drafts',
    label: 'Drafts',
    sortOrder: 1,
    filters: ['Draft', 'Ready'],
  },
  {
    id: 'Scheduled',
    label: 'Scheduled',
    sortOrder: 2,
    filters: ['Scheduled'],
  },
  {
    id: 'Live',
    label: 'Live',
    sortOrder: 3,
    filters: ['Recurring', 'Running', 'Recalling'],
  },
  {
    id: 'Finished',
    label: 'Finished',
    sortOrder: 4,
    filters: ['Finished', 'Aborted', 'Recalled'],
  },
  {
    id: 'Archived',
    label: 'Archived',
    sortOrder: 5,
    filters: ['Archived'],
  },
];

export const CAMPAIGN_HEADER_GRIDS = `
  ${pxToRem(48)} // select checkbox
  minmax(${pxToRem(276)}, 1fr) // name
  ${pxToRem(120)} // status
  ${pxToRem(150)} // delivery
  ${pxToRem(150)} // engagement
  ${pxToRem(160)} // launch date
  ${pxToRem(220)} // last modified
  ${pxToRem(36)}  // overflow menu button
`;

export const CAMPAIGN_GRIDS = `
  ${pxToRem(48)} // select checkbox
  ${pxToRem(60)} // badge
  minmax(${pxToRem(200)}, 1fr) // name
  ${pxToRem(120)} // status
  ${pxToRem(150)} // delivery
  ${pxToRem(150)} // engagement
  ${pxToRem(160)} // launch date
  ${pxToRem(220)} // last modified
  ${pxToRem(36)}  // overflow menu button
`;

export const experimentMap = {
  EmailBody: 'email body.',
  FrequencyOptimization: 'frequency optimization.',
  FromNameAndSender: 'from name and sender.',
  PreheaderText: 'preheader text.',
  SendTime: 'send time.',
  SendTimeOptimization: 'Send Time Optimization.',
  SubjectLine: 'the subject line.',
  WholeEmail: 'all content.',
};

export const aiMap = {
  ChannelOptimization: 'Channel Optimization.',
  FrequencyOptimization: 'Frequency Optimization.',
  SendTimeOptimization:
    'It uses Send Time Optimization, which sends messages when your contacts are more likely to open and read them.',
  SendTimeOptimizationExperiment:
    "It has an associated experiment that uses Send Time Optimization for half of its contacts and the campaign's scheduled send time for everyone else.",
};

export const messageMediumMap = {
  [EMAIL]: 'Email',
  [IN_APP]: 'In-app',
  [SMS]: 'SMS',
  [PUSH]: 'Push',
  [WEB_PUSH]: 'Web push',
  [EmbeddedMessage]: 'Embedded',
  [WhatsApp]: 'WhatsApp',
};

export const campaignTypeMap = {
  ApiTriggered: 'API-Triggered',
  Blast: 'Blast',
  WithAI: 'With AI',
  WithExperiment: 'With experiment',
  WorkflowTriggered: 'Journey triggered',
};

export const campaignTypeMapWithEligibility = {
  ...campaignTypeMap,
  Eligibility: 'Eligibility',
};

export const SORT_DIRECTIONS = {
  ASCENDING: 'Ascending',
  DESCENDING: 'Descending',
};

export const MODAL_DISPLAY_TEXT_FOR_DELETING_CAMPAIGNS = {
  actionName: {
    verb: 'delete',
    adjective: 'deleted',
  },
  itemTypeName: {
    singular: 'campaign',
    plural: 'campaigns',
  },
  actionIcon: Delete,
};

export const MODAL_DISPLAY_TEXT_FOR_DELETING_FOLDER = {
  ...MODAL_DISPLAY_TEXT_FOR_DELETING_CAMPAIGNS,
  suffixText: 'This will also archive its contents.',
};

export const CONTENT_ITEM_NAME = 'campaign';

export const FOLDER_ID_SEARCH_PARAM = 'folderId';
export const PAGE_SEARCH_PARAM = 'page';

export const PARENT_CAMPAIGN_ID_SEARCH_PARAM = 'parentCampaignId';
export const UNSET_PARENT_CAMPAIGN_ID = -1;

export enum QUERY_PARAMS {
  CHANNELS = 'channels',
  CREATORS = 'creators',
  LABELS = 'labels',
  LISTS = 'lists',
  MEDIA = 'media',
  MODIFIERS = 'modifiers',
  SEARCH = 'search',
  STATE_CATEGORY = 'stateCategory',
  STATES = 'states',
  TYPES = 'types',
  PLACEMENTS = 'placements',
  ALERTS = 'alerts',
  TEMPLATE_TYPES = 'templateTypes',
  TEMPLATE_STATUSES = 'templateStatuses',
  TEMPLATE_QUALITY_RATINGS = 'templateQualityRatings',
  IS_FAVORITES = 'isFavorites',
  HIDE_FOLDERS = 'hideFolders',
}

export enum AlertFilterType {
  NEW_ALERT_TRIGGERED = 'RecentAlert',
  ANY_ALERT_TRIGGERED = 'AnyAlert',
  ALERTS_ENABLED = 'AlertsEnabled',
}

/**
 * Campaigns that have not been scheduled or activated
 */
export const PRE_LAUNCH_CAMPAIGN_STATES = [
  CampaignStateDefinitions.Draft,
  CampaignStateDefinitions.Ready,
];

export const ALERT_OPTIONS = [
  {
    id: AlertFilterType.NEW_ALERT_TRIGGERED,
    label: 'New alert triggered',
  },
  {
    id: AlertFilterType.ANY_ALERT_TRIGGERED,
    label: 'Any alert triggered',
  },
  {
    id: AlertFilterType.ALERTS_ENABLED,
    label: 'Alerts enabled',
  },
];

export const TEMPLATE_TYPE_OPTIONS = [
  {
    id: TT.BusinessInitiated,
    label: 'Business-initiated',
  },
  {
    id: TT.UserInitiated,
    label: 'User-initiated',
  },
];

const WHATSAPP_QUALITY_RATING_OPTIONS = [
  MetaQualityRating.Unknown,
  MetaQualityRating.High,
  MetaQualityRating.Medium,
  MetaQualityRating.Low,
].map((qualityRating) => ({
  label: getQualityRatingLabel(qualityRating),
  id: qualityRating,
}));

export const WHATSAPP_STATUS_OPTIONS = (isWhatsAppGAEnabled: boolean) =>
  [
    MetaApprovalStatusDefinition.Approved,
    PseudoApprovalStatusDefinition.Paused,
    MetaApprovalStatusDefinition.Disabled,
  ].map((status) => ({
    label: status,
    id: status,
    ...(isWhatsAppGAEnabled &&
      status === MetaApprovalStatusDefinition.Approved && {
        prefix: FILTER_PREFIXES.WHATSAPP_QUALITY_RATING,
        isExpandableGroup: true,
        options: WHATSAPP_QUALITY_RATING_OPTIONS.map(({ id, label }) => ({
          id: `${FILTER_PREFIXES.WHATSAPP_QUALITY_RATING}${id}`,
          label,
        })),
      }),
  }));
